import React, { useState } from 'react';
const API_URL = process.env.REACT_APP_API_URL;

// Helper to get valid admin access token, refresh if needed
async function getAdminAccessToken() {
  let accessToken = localStorage.getItem('gogig_admin_access');
  const refreshToken = localStorage.getItem('gogig_admin_refresh');
  if (accessToken) {
    try {
      const res = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (res.status !== 401) return accessToken;
    } catch {}
  }
  if (refreshToken) {
    try {
      const res = await fetch(`${API_URL}/api/admin/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });
      const data = await res.json();
      if (data.success && data.accessToken) {
        localStorage.setItem('gogig_admin_access', data.accessToken);
        return data.accessToken;
      }
    } catch {}
  }
  localStorage.removeItem('gogig_admin_access');
  localStorage.removeItem('gogig_admin_refresh');
  window.location.href = '/admin';
  return null;
}

async function adminFetch(url, options = {}) {
  const token = await getAdminAccessToken();
  if (!token) throw new Error('Not authenticated');
  const headers = { ...(options.headers || {}), Authorization: `Bearer ${token}` };
  return fetch(url, { ...options, headers });
}

function AdminReferredCandidatesStipendReport({ apiBase, getToken, fetch: fetchProp }) {
  const getTokenFn = getToken || getAdminAccessToken;
  const fetchFn = fetchProp || adminFetch;
  const basePath = apiBase ? `${API_URL}/${apiBase}` : `${API_URL}/api/admin`;
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [report, setReport] = useState([]);
  const [diagnostics, setDiagnostics] = useState(null);
  const [stipendWindow, setStipendWindow] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchReport = async () => {
    setLoading(true);
    setError('');
    setDiagnostics(null);
    try {
      const url = `${basePath}/referred-candidates-stipend-report?year=${year}`;
      const res = await fetchFn(url);
      const data = await res.json();
      if (data.success) {
        setReport(data.report || []);
        setDiagnostics(data.diagnostics || null);
        setStipendWindow(data.stipend_window || data.diagnostics?.stipend_window || '');
      } else {
        setError(data.message || data.error || 'Failed to fetch report');
      }
    } catch (err) {
      setError('Failed to fetch report: ' + err.message);
    }
    setLoading(false);
  };

  const handleDownloadCSV = async () => {
    const token = await getTokenFn();
    if (!token) return;
    const url = `${basePath}/referred-candidates-stipend-report?year=${year}&format=csv`;
    try {
      const res = await fetchFn(url);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = `referred_candidates_stipend_report_${year}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(a.href);
    } catch (err) {
      setError('Download failed: ' + err.message);
    }
  };

  const MONTH_NAMES = {
    1: 'January', 2: 'February', 3: 'March', 4: 'April', 5: 'May', 6: 'June',
    7: 'July', 8: 'August', 9: 'September', 10: 'October', 11: 'November', 12: 'December',
  };

  const formatMonthChip = (r, monthOrLabel, idx) => {
    if (typeof monthOrLabel === 'string' && monthOrLabel.includes(' ')) return monthOrLabel;
    const labels = r.months_in_stipend_labels || [];
    if (labels[idx]) return labels[idx];
    return MONTH_NAMES[monthOrLabel] || `Month ${monthOrLabel}`;
  };

  const windowText = stipendWindow || diagnostics?.stipend_window || 'the past 3 completed months';

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
      <h2 style={{ fontWeight: 800, fontSize: 24, color: '#5a4ff3', marginBottom: 18 }}>
        Referred Candidates Stipend Report
      </h2>

      <div style={{
        background: '#f8f9fa',
        borderRadius: 10,
        padding: 16,
        marginBottom: 24,
        border: '1px solid #e0e0e0'
      }}>
        <p style={{ margin: 0, color: '#666', fontSize: 14 }}>
          This report shows referred candidates who are currently employed and appear in stipend sheets
          for the <strong>past 3 completed months</strong>
          {stipendWindow ? <> (<strong>{stipendWindow}</strong>)</> : null}.
          Includes app signups with a referral code <strong>and supervisor/SPOC-uploaded candidates</strong> that have a referral code.
          Stipend matching is by mobile number after accounts exist and stipend sheets are uploaded.
          The year filter applies to when the referral/upload happened.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 18, alignItems: 'center' }}>
        <label style={{ fontWeight: 600, color: '#374151' }}>Year:</label>
        <input
          type="number"
          placeholder="Year"
          value={year}
          onChange={e => setYear(e.target.value)}
          className="gogig-input"
          style={{ width: 120 }}
        />
        <button
          className="gogig-btn"
          onClick={fetchReport}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Fetch Report'}
        </button>
        <button
          className="gogig-btn-outline"
          onClick={handleDownloadCSV}
          disabled={loading || !report.length}
        >
          Download CSV
        </button>
      </div>

      {error && (
        <div style={{
          color: '#ef4444',
          marginBottom: 12,
          padding: 12,
          background: '#fee2e2',
          borderRadius: 8,
          border: '1px solid #fecaca'
        }}>
          {error}
        </div>
      )}

      {report.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <strong style={{ color: '#374151' }}>
            Total Candidates: {report.length}
          </strong>
          {stipendWindow ? (
            <span style={{ marginLeft: 12, color: '#64748b', fontSize: 14 }}>
              Stipend window: {stipendWindow}
            </span>
          ) : null}
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          background: '#fff',
          borderRadius: 10,
          boxShadow: '0 2px 8px rgba(90,79,243,0.07)',
          marginBottom: 18,
          borderCollapse: 'collapse'
        }}>
          <thead>
            <tr style={{ background: '#f3f4f6' }}>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Referral Code</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Candidate Name</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Mobile</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Email</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Referral Date</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Months Active From Referral</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Months in Last 3 Months</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Months in Stipend</th>
            </tr>
          </thead>
          <tbody>
            {report.map((r, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: 12, fontWeight: 600, color: '#5a4ff3' }}>{r.referral_code}</td>
                <td style={{ padding: 12 }}>{r.candidate_name || '-'}</td>
                <td style={{ padding: 12 }}>{r.candidate_mobile || '-'}</td>
                <td style={{ padding: 12 }}>{r.candidate_email || '-'}</td>
                <td style={{ padding: 12 }}>{r.referral_date || '-'}</td>
                <td style={{ padding: 12, textAlign: 'center' }}>
                  <span style={{
                    background: '#dbeafe',
                    color: '#1e40af',
                    padding: '4px 8px',
                    borderRadius: 6,
                    fontWeight: 600
                  }}>
                    {r.months_active_from_referral}
                  </span>
                </td>
                <td style={{ padding: 12, textAlign: 'center' }}>
                  <span style={{
                    background: '#dcfce7',
                    color: '#166534',
                    padding: '4px 8px',
                    borderRadius: 6,
                    fontWeight: 600
                  }}>
                    {r.months_in_last_3_months}
                  </span>
                </td>
                <td style={{ padding: 12 }}>
                  {(r.months_in_stipend_labels || r.months_in_stipend || []).length > 0 ? (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {(r.months_in_stipend_labels || r.months_in_stipend).map((month, idx) => (
                        <span
                          key={`${month}-${idx}`}
                          style={{
                            background: '#fef3c7',
                            color: '#92400e',
                            padding: '4px 8px',
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 500
                          }}
                        >
                          {formatMonthChip(r, month, idx)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!loading && !report.length && !error && (
        <div style={{
          textAlign: 'center',
          color: '#64748b',
          padding: 40,
          background: '#f8fafc',
          borderRadius: 12,
          border: '1px solid #e2e8f0'
        }}>
          <p style={{ margin: '0 0 8px', fontWeight: 600 }}>No candidates in report for year {year}.</p>
          <p style={{ margin: 0, fontSize: 14 }}>Click &quot;Fetch Report&quot; to load data.</p>
        </div>
      )}

      {!loading && report.length === 0 && diagnostics && (
        <div style={{
          marginTop: 16,
          padding: 16,
          background: '#f0fdfa',
          borderRadius: 12,
          border: '1px solid #99f6e4',
          fontSize: 14,
          color: '#0f766e'
        }}>
          <strong>Why empty?</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            <li>Referred candidates in {year}: <strong>{diagnostics.referral_uses_count}</strong>
              {diagnostics.supervisor_upload_rows != null ? (
                <> (app: {diagnostics.referral_use_rows || 0}, supervisor upload: {diagnostics.supervisor_upload_rows || 0})</>
              ) : null}
            </li>
            <li>Employed (accepted application or contract/stipend doc): <strong>{diagnostics.employed_count}</strong></li>
            <li>Found in stipend sheets ({windowText}): <strong>{diagnostics.in_stipend_count}</strong></li>
          </ul>
          <p style={{ margin: '12px 0 0', fontSize: 13 }}>
            The report only includes referred candidates who are employed and appear in at least one stipend sheet
            for the past 3 completed months ({windowText}).
            If referral_uses_count is 0, no one used a referral code in {year}.
            If employed_count is 0, referred candidates are not marked employed.
            If in_stipend_count is 0, their mobiles may not match stipend uploads for {windowText}.
          </p>
        </div>
      )}
    </div>
  );
}

export default AdminReferredCandidatesStipendReport;
